// O alvo da regra, ponta a ponta: os tipos que a rota aceita, a normalização do
// contrato, o recorte que vai gravado e o passo 1 do editor (espec. 8.3).
// Apresentacional — quem busca, guarda e escolhe é o `RuleEditorOffcanvas`.
import { money } from "./format";

// Tipos de alvo da espec. 8.3 já resolvidos nos valores de `?level=` da rota
// `GET .../disbursement/targets`. "Nível da EAP" não é um valor de query: ele abre
// um segundo seletor com os níveis reais da árvore.
export const TARGET_TYPES = Object.freeze([
  { value: "erp_group", label: "Grupo do ERP" },
  { value: "ampli_group", label: "Grupo Ampli" },
  { value: "resource", label: "Insumo" },
  { value: "node", label: "Nível da EAP" },
]);

export const NODE_LEVELS = Object.freeze([
  { value: "unit", label: "Unidade" },
  { value: "cell", label: "Célula" },
  { value: "step", label: "Etapa" },
  { value: "substep", label: "Subetapa" },
  { value: "service", label: "Serviço" },
]);

/**
 * Níveis que a rota `GET .../disbursement/targets` aceita em `?level=` (espec. 8.2).
 * `resource_group` — o `kind` que a UI usa para "grupo de insumo" — NÃO está aqui: a
 * rota separa grupo do ERP de grupo Ampli, e é a CHAVE do alvo que diz qual dos dois.
 */
export const TARGET_QUERY_LEVELS = Object.freeze([
  "erp_group",
  "ampli_group",
  "resource",
  "unit",
  "cell",
  "step",
  "substep",
  "service",
]);

const AMPLI_GROUP_KEY = /^resource_group:ampli:/;

const GROUP_LEVELS = Object.freeze(["resource_group", "erp_group", "ampli_group"]);

/**
 * Nível de CONSULTA — o valor que vai em `?level=` na rota de alvos. Só existe para a
 * busca: `erp_group`/`ampli_group` são recortes do PICKER, e a rota separa os dois.
 * Derivado na hora do fetch a partir do `kind` + da CHAVE do alvo (é a chave que diz
 * se o grupo é do ERP ou Ampli), nunca gravado.
 *
 * `null` = não há nível derivável. Nesse caso o chamador NÃO consulta: a rota rejeita
 * um `?level=` ausente ou inválido com 400 (`invalid_target_level`), então uma
 * consulta sem filtro não é "mais tolerante", é um erro calado.
 */
export const targetQueryLevel = (target) => {
  if (!target) return null;
  if (target.kind === "resource_group" || GROUP_LEVELS.includes(target.level)) {
    return AMPLI_GROUP_KEY.test(String(target.key ?? "")) ? "ampli_group" : "erp_group";
  }
  if (target.kind === "resource") return "resource";
  if (TARGET_QUERY_LEVELS.includes(target.level)) return target.level;
  return null;
};

/**
 * Nível de ARMAZENAMENTO — o `level` que vai gravado dentro de `rule.target`.
 *
 * O vocabulário aqui é o do back-end (`rulePlanner.js#LEVELS_BY_KIND`), e ele NÃO é o
 * do picker: `kind: "resource_group"` aceita um único nível, `"resource_group"`.
 * Mandar `erp_group`/`ampli_group` na gravação é 400 `invalid_target` ("Nível do alvo
 * inválido") — por isso a derivação do nível de consulta acontece só no fetch.
 *
 *   | kind             | level gravado                                  |
 *   |------------------|------------------------------------------------|
 *   | `resource_group` | `resource_group`                               |
 *   | `resource`       | `resource`                                     |
 *   | `node`           | `unit`/`cell`/`step`/`substep`/`service`/`building` |
 */
export const ruleTargetLevel = (target) => {
  if (!target) return null;
  if (target.kind === "resource_group") return "resource_group";
  if (target.kind === "resource") return "resource";
  if (target.kind === "node") return target.level ?? null;
  // Alvo sem `kind` (chamador antigo): o nível é a única pista que sobra.
  if (GROUP_LEVELS.includes(target.level)) return "resource_group";
  return target.level ?? null;
};

/**
 * Normaliza o alvo vindo da rota (ou de um chamador antigo) numa forma só:
 * `plannedCents` e `plannedSeries: [{ month, cents }]`. O contrato do cliente de API
 * usa esses nomes; `plannedTotalCents`/`competence` são aceitos por tolerância.
 */
export const normalizeTarget = (target) => {
  if (!target) return null;
  const planned = target.plannedCents ?? target.plannedTotalCents;
  const series = Array.isArray(target.plannedSeries) ? target.plannedSeries : [];
  return {
    ...target,
    // O `level` normalizado é o de ARMAZENAMENTO: é este objeto que alimenta
    // `ruleTargetOf` e vai no corpo do POST/PATCH. A rota devolve os alvos de grupo
    // com `level: "erp_group"`/`"ampli_group"` (o recorte que o picker pediu), e
    // gravar aquilo seria 400 — o nível de consulta é rederivado no fetch por
    // `targetQueryLevel`, que lê `kind` + `key` e não depende deste campo.
    level: ruleTargetLevel(target) ?? target.level,
    plannedCents: Number.isFinite(planned) ? planned : null,
    plannedSeries: series
      .map((point) => ({ month: point?.month ?? point?.competence ?? null, cents: point?.cents }))
      .filter((point) => point.month && Number.isFinite(point.cents)),
  };
};

/** O alvo gravado na regra são só estes quatro campos (espec. 4.1). */
export const ruleTargetOf = (target) => (target ? {
  kind: target.kind,
  key: target.key,
  level: target.level,
  label: target.label,
} : null);

/**
 * Valor previsto do alvo. Existe como componente PRÓPRIO porque `money` chama
 * `formatNumberToCurrency`, que é um hook disfarçado (`useProjectContext`): chamado
 * dentro de um laço ou de um galho condicional, ele muda a ordem de hooks do
 * componente hospedeiro quando a contagem de alvos (ou o passo do editor) muda, e o
 * React avisa em dev. Isolado aqui, cada instância chama o hook exatamente uma vez.
 *
 * O `span` interno é o contrato do módulo (ver `DisbursementList`): o reset global de
 * `span` em `_fonts.scss` exige regra explícita para o valor monetário — Task 22.
 */
export const TargetValue = ({ cents }) => (
  <span className="ampli-rule-editor__target-value">
    <span>{money(cents)}</span>
  </span>
);

const RuleTargetPicker = ({
  targetType,
  nodeLevel,
  search,
  candidates,
  loading,
  error,
  onChangeTargetType,
  onChangeNodeLevel,
  onChangeSearch,
  onChoose,
}) => (
  <section aria-label="Escolha do alvo">
    <h3>Qual alvo essa regra cobre?</h3>
    <div className="ampli-rule-editor__filters">
      <label className="ampli-rule-editor__field">
        <span className="card-label">Tipo de alvo</span>
        <select
          aria-label="Tipo de alvo"
          value={targetType}
          onChange={(event) => onChangeTargetType(event.target.value)}
        >
          {TARGET_TYPES.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </label>
      {targetType === "node" ? (
        <label className="ampli-rule-editor__field">
          <span className="card-label">Nível da EAP</span>
          <select
            aria-label="Nível da EAP"
            value={nodeLevel}
            onChange={(event) => onChangeNodeLevel(event.target.value)}
          >
            {NODE_LEVELS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
      ) : null}
      <label className="ampli-rule-editor__field">
        <span className="card-label">Buscar alvo</span>
        <input
          type="search"
          aria-label="Buscar alvo"
          value={search}
          onChange={(event) => onChangeSearch(event.target.value)}
          placeholder="Nome do grupo, insumo ou etapa"
        />
      </label>
    </div>

    {loading ? <p className="card-label">Carregando…</p> : null}
    {error && !loading ? (
      <p className="alert alert-error" role="alert">
        Não foi possível carregar os alvos. Tente novamente.
      </p>
    ) : null}
    {!loading && !error && candidates.length === 0 ? (
      <p className="empty-state">Nenhum alvo com previsto neste filtro.</p>
    ) : null}

    <ul className="ampli-rule-editor__targets">
      {candidates.map((candidate) => (
        <li key={candidate.key} data-testid={`target-${candidate.key}`}>
          <button type="button" onClick={() => onChoose(candidate)}>
            <strong className="ampli-rule-editor__target-label">{candidate.label}</strong>
            {candidate.path ? (
              <span className="ampli-rule-editor__target-path">{candidate.path}</span>
            ) : null}
            <span className="ampli-rule-editor__target-count">
              {`${candidate.itemCount ?? 0} itens`}
            </span>
            <TargetValue cents={candidate.plannedCents} />
            {candidate.hasRule ? (
              <span className="ampli-rule-editor__target-has-rule">já tem regra</span>
            ) : null}
          </button>
        </li>
      ))}
    </ul>
  </section>
);

export default RuleTargetPicker;
